import {pillowLayout} from './pillow-layout'
import { isXGuardrail } from '../../../../../config/blissGuardrails'
import {createBeddingSet} from './bedding-model'
import type {BeddingSelection} from '../../../../../config/blissBedding'
import { rectangularFace } from './face-panel-model'
import type { SurfaceFaceStyle } from '../../../../../config/blissRoomFinish'
import { mattressThickness, type MattressThickness } from '../../../../../config/blissRoomFinish'
import { createDrawerFace } from './drawer-face'
import { addBedTrim } from './bed-trim-model'
import type { DrawerPullStyle, BedTrimStyle, DrawerFaceStyle } from '../../../../../config/blissRoomFinish'
import * as T from 'three'
import { createPillow, fabricTexture } from './soft-bedding'
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js'
import { Bed, bounds } from './beds'
import { Room } from './model'
import { bunkProfile, bunkGuardrailProfile } from './bunk-profile'
import { BUNK_CORNER_FACE, bunkJoinery } from './bunk-joinery'
import type { FinishPart } from './finish-materials'
import { X_GUARDRAIL, type BasicGuardrailStyle } from '../../../../../config/blissGuardrails'
import { createXGuardrails } from './x-guardrail-model'

// Visual planning model. The saved footprint includes the reserved end ladder.
export function createBunkModel(
  b: Bed,
  r: Room,
  guardBars: 2 | 3 = 2,
  bedDrawers = false,
  guardrailStyle: BasicGuardrailStyle = 'horizontal',
  drawerFaceStyle: DrawerFaceStyle = 'shaker',
  drawerPullStyle?: DrawerPullStyle,
  bedTrim?: BedTrimStyle,
  mattresses?: MattressThickness,
  upperBunkFaceStyle: SurfaceFaceStyle = 'flat',
  beddingSelection?: BeddingSelection
) {
  if (guardrailStyle === 'horizontal_3bar') guardBars = 3
  const root = new T.Group(),
    q = bounds(b),
    w = b.width,
    d = b.depth,
    h = b.height
  const {
    thickness: t,
    ladderDepth,
    frameLength,
    lower,
    upper,
    pocket,
    offset,
    entryX,
    entryWidth,
  } = bunkJoinery(b)
  const oakFrame = 0xe2cea8,
    mattress = 0xfaf6f0
  function box(
    name: string,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    finishPart: FinishPart | null = 'frame',
    color = oakFrame,
    rounded = false
  ) {
    const geometry = rounded
      ? new RoundedBoxGeometry(
          width / 12,
          height / 12,
          depth / 12,
          3,
          Math.min(0.07, width / 48, height / 48, depth / 48)
        )
      : new T.BoxGeometry(width / 12, height / 12, depth / 12)
    const mesh = new T.Mesh(
      geometry,
      new T.MeshStandardMaterial({ color, roughness: rounded ? 0.65 : 0.72, metalness: 0 })
    )
    mesh.name = name
    mesh.position.set(
      (x + width / 2 - w / 2) / 12,
      (y + height / 2) / 12,
      (z + depth / 2 - d / 2) / 12
    )
    if (finishPart) mesh.userData.finishPart = finishPart
    if (finishPart === 'topRail') mesh.userData.railLevel = guardBars
    if (rounded && !finishPart) mesh.material.map = fabricTexture()
    root.add(mesh)
    if (!rounded) {
      mesh.add(
        new T.LineSegments(
          new T.EdgesGeometry(geometry),
          new T.LineBasicMaterial({ color: 0x9a8268, transparent: true, opacity: 0.32 })
        )
      )
    }
    return mesh
  }
  const { upperY, beamH, mattressH, rungs } = bunkProfile(h)
  const lowerY = bedDrawers ? 18 : bunkProfile(h).lowerY
  const {
    face: barHeight,
    gap,
    base: guardBase,
    top: guardTop,
  } = bunkGuardrailProfile(h, guardBars)
  const xBase = upperY
  // X panels bear directly on the upper frame; horizontal rails are backed by uprights.
  const postTop = isXGuardrail(guardrailStyle) ? xBase : guardTop

  function corner(
    name: string,
    frame: typeof lower,
    right: boolean,
    foot: boolean,
    base: number,
    top: number
  ) {
    if (top <= base) return
    const face = Math.min(BUNK_CORNER_FACE, frame.width / 3)
    const outerX = right ? frame.x + frame.width : frame.x
    const outerZ = foot ? frameLength : 0
    // End upright covers the corner; side upright butts behind it. Horizontal
    // side rails run past the end rails, staggering the two layers' seams.
    box(
      `${name}-end`,
      right ? outerX - face : outerX,
      base,
      foot ? outerZ - t : outerZ,
      face,
      top - base,
      t
    )
    box(
      `${name}-side`,
      right ? outerX - t : outerX,
      base,
      foot ? outerZ - face : outerZ + t,
      t,
      top - base,
      face - t
    )
  }
  for (const foot of [false, true]) {
    corner(`corner-left-${foot ? 'foot' : 'head'}`, lower, false, foot, 0, postTop)
    corner(
      `corner-right-${foot ? 'foot' : 'head'}`,
      lower,
      true,
      foot,
      0,
      offset ? upperY : postTop
    )
    if (offset)
      corner(`corner-upper-right-${foot ? 'foot' : 'head'}`, upper, true, foot, upperY, postTop)
  }


  for (const [index, frame] of [lower, upper].entries()) {
    const y = index === 0 ? lowerY : upperY,
      inside = pocket(frame),
      name = index === 0 ? 'lower' : 'upper'
    for (const [side, x] of [
      ['left', frame.x + t],
      ['right', frame.x + frame.width - 2 * t],
    ] as const) {
      // Basic lower side panels meet the floor. Keep the drawer opening usable.
      const base = index === 0 && !(bedDrawers && side === 'right') ? 0 : y - beamH
      box(`${name}-frame-${side}`, x, base, t, t, y - base, frameLength - 2 * t)
      if (index === 0 && bedDrawers && side === 'right')
        box('lower-drawer-plinth', x, 0, t, t, 1, frameLength - 2 * t)
    }
    for (const [end, z] of [
      ['head', t],
      ['foot', frameLength - 2 * t],
    ] as const) {
      const base = index === 0 ? 0 : y - beamH
      box(`${name}-frame-${end}`, inside.x, base, z, inside.width, y - base, t)
      // Offset upper bunks bear on end beams reaching the outside lower supports.
      if (index === 1 && offset)
        box(
          `${name}-frame-${end}-extension`,
          upper.x + upper.width - t,
          y - beamH,
          z,
          lower.width - upper.width - t,
          beamH,
          t
        )
    }
    box(`${name}-deck`, inside.x, y - t, inside.z, inside.width, t, inside.length)
    if(index===1 && upperBunkFaceStyle!=='flat') {
      for(const [edge,x,z,width,angle] of [
        ['left',frame.x+t,t,frameLength-2*t,-Math.PI/2],
        ['right',frame.x+frame.width-t,frameLength-t,frameLength-2*t,Math.PI/2],
        ['head',inside.x+inside.width,t,inside.width,Math.PI],
        ['foot',inside.x,frameLength-t,inside.width,0],
      ] as const) {
        const face=rectangularFace(width,beamH,upperBunkFaceStyle)
        face.name=`upper-${edge}-face-${upperBunkFaceStyle}`
        face.rotation.y=angle
        face.position.set((x-w/2)/12,(y-beamH)/12,(z-d/2)/12)
        root.add(face)
      }
    }
    addBedTrim(root,{x:frame.x,y:0,w:frame.width,h:frameLength},y,index===0,bedTrim,b,['front'])
    const thickness = mattressThickness(mattresses,index===0?"lower":"upper",mattressH)
    box(
      `${name}-mattress`,
      inside.x + 0.5,
      y,
      inside.z + 0.5,
      inside.width - 1,
      thickness,
      inside.length - 1,
      null,
      mattress,
      true
    )
    const beddingId=beddingSelection?.[index===0?'lower':'upper']??'original'
    if(beddingId!=='original'){
      const set=createBeddingSet(inside.width-1,inside.length-1,beddingId,index!==0,beddingSelection)
      set.name=`${name}-bedding-${beddingId}`
      set.position.set((inside.x+inside.width/2-w/2)/12,(y+thickness)/12,(inside.z+.5-d/2)/12)
      root.add(set)
    }else {
    const {count,width:pillowW,depth:pillowD,offsets}=pillowLayout(inside.width)
    for (let i = 0; i < count; i++) {
      const pillow = createPillow(pillowW, pillowD, 5)
      pillow.name = `${name}-pillow`
      pillow.position.set(
        (inside.x + inside.width/2 + offsets[i] - w / 2) / 12,
        (y + thickness + 2.5) / 12,
        (inside.z + pillowD * 0.65 - d / 2) / 12
      )
      root.add(pillow)
    }
    }
  }

  if (bedDrawers) {
    const inside = pocket(lower),
      drawerLength = (inside.length - 1.5) / 2
    for (let i = 0; i < 2; i++) {
      const z = inside.z + 0.5 + i * (drawerLength + 0.5)
      box(`drawer-${i}-body`, inside.x, 1, z, inside.width - t, lowerY - beamH - 1.5, drawerLength)
      const face = createDrawerFace(drawerLength, lowerY - beamH - 1.5, drawerFaceStyle, drawerPullStyle)
      face.rotation.y = Math.PI/2
      face.position.set((inside.x+inside.width-t-w/2)/12,1/12,(z+drawerLength-d/2)/12)
      root.add(face)
    }
  }
  const inside = pocket(upper)
  if (isXGuardrail(guardrailStyle)) root.add(createXGuardrails(b, xBase, guardrailStyle === 'x_pattern_front'))
  else
    for (let i = 0; i < guardBars; i++) {
      const y = guardBase + i * (barHeight + gap)
      for (const [side, x] of [
        ['left', upper.x + t],
        ['right', upper.x + upper.width - 2 * t],
      ] as const)
        box(`guard-${i}-${side}`, x, y, t, t, barHeight, frameLength - 2 * t, i === guardBars - 1 ? 'topRail' : i === 0 ? 'rail1' : 'rail2')
      box(`guard-${i}-head`, inside.x, y, t, inside.width, barHeight, t, i === guardBars - 1 ? 'topRail' : i === 0 ? 'rail1' : 'rail2')
      // Keep the foot entry clear for either ladder or stairs, opposite the pillows.
      if (entryX > inside.x)
        box(
          `guard-${i}-foot-left`,
          inside.x,
          y,
          frameLength - 2 * t,
          entryX - inside.x,
          barHeight,
          t,
          i === guardBars - 1 ? 'topRail' : i === 0 ? 'rail1' : 'rail2'
        )
      const right = entryX + entryWidth
      if (inside.x + inside.width > right)
        box(
          `guard-${i}-foot-right`,
          right,
          y,
          frameLength - 2 * t,
          inside.x + inside.width - right,
          barHeight,
          t,
          i === guardBars - 1 ? 'topRail' : i === 0 ? 'rail1' : 'rail2'
        )
    }
  // Back the cut return ends with full-height uprights attached to the foot beam.
  // They also meet the backs of the ladder sides; no return ends float above the ladder.
  if (!isXGuardrail(guardrailStyle)) {
    const right = entryX + entryWidth
    const leftFace = Math.min(BUNK_CORNER_FACE, entryX - inside.x)
    const rightFace = Math.min(BUNK_CORNER_FACE, inside.x + inside.width - right)
    if (leftFace > 0)
      box(
        'entry-support-left',
        entryX - leftFace,
        upperY - beamH,
        frameLength - t,
        leftFace,
        guardTop - upperY + beamH,
        t
      )
    if (rightFace > 0)
      box(
        'entry-support-right',
        right,
        upperY - beamH,
        frameLength - t,
        rightFace,
        guardTop - upperY + beamH,
        t
      )
  }
  const bottom = pocket(lower)
  for (const [end, z] of [
    ['head', t],
    ['foot', frameLength - 2 * t],
  ] as const)
    box(
      `lower-${end}-panel`,
      bottom.x,
      lowerY + mattressH + h * 0.035,
      z,
      bottom.width,
      h * 0.06,
      t,
      'accent'
    )

  if (b.ladder !== false) {
    const stile = Math.min(2.25, w * 0.04)
    const ladderTop = isXGuardrail(guardrailStyle) ? xBase + X_GUARDRAIL.height : guardTop
    for (const [i, x] of [entryX - stile, entryX + entryWidth].entries())
      box(`ladder-stile-${i}`, x, 0, frameLength, stile, ladderTop, ladderDepth)
    rungs.forEach((y, i) =>
      box(
        `ladder-rung-${i}`,
        entryX,
        y,
        frameLength,
        entryWidth,
        Math.min(1.5, h * 0.025),
        ladderDepth,
        'accent'
      )
    )
  }
  root.position.set((q.x + q.w / 2 - r.width / 2) / 12, 0, (q.y + q.h / 2 - r.depth / 2) / 12)
  root.rotation.y = (b.rotation * Math.PI) / 180
  root.name = `${b.name} · reference-inspired planning model`
  return root
}




